import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";
import { CheckIcon } from "lucide-react";

enum ENUM_EMOJI_SCORE {
  VERY_BAD = 1,
  BAD = 2,
  NEUTRAL = 3,
  GOOD = 4,
  EXCELLENT = 5,
}

const EMOJI_OPTIONS: {
  score: ENUM_EMOJI_SCORE;
  emoji: string;
  label: string;
}[] = [
  {
    score: ENUM_EMOJI_SCORE.VERY_BAD,
    emoji: "😔",
    label: "company:performance_review.rating_very_bad",
  },
  {
    score: ENUM_EMOJI_SCORE.BAD,
    emoji: "😕",
    label: "company:performance_review.rating_bad",
  },
  {
    score: ENUM_EMOJI_SCORE.NEUTRAL,
    emoji: "😐",
    label: "company:performance_review.rating_neutral",
  },
  {
    score: ENUM_EMOJI_SCORE.GOOD,
    emoji: "🙂",
    label: "company:performance_review.rating_good",
  },
  {
    score: ENUM_EMOJI_SCORE.EXCELLENT,
    emoji: "😄",
    label: "company:performance_review.rating_excellent",
  },
];

interface EmojiRatingProps {
  selected: ENUM_EMOJI_SCORE;
  /** Pass onChange to make it interactive; omit for read-only */
  onChange?: (score: ENUM_EMOJI_SCORE) => void;
  isReviewMode?: boolean;
}

export const EmojiRating = ({
  selected,
  onChange,
  isReviewMode = false,
}: EmojiRatingProps) => {
  const { t } = useTranslation(["common", "company"]);
  const isReadOnly = !onChange;

  return (
    <div className="flex items-center gap-3">
      {EMOJI_OPTIONS.map(({ score, emoji, label }) => {
        const isSelected = selected === score;
        return (
          <button
            key={score}
            type="button"
            title={t(label)}
            disabled={isReadOnly}
            onClick={() => onChange?.(score)}
            className={cn(
              "relative flex items-center justify-center h-11 w-11 rounded-full text-2xl transition-all duration-150 bg-white",
              isReadOnly ? "cursor-default" : "cursor-pointer hover:scale-110",
              isSelected
                ? "ring-2 ring-primary ring-offset-1 bg-primary/5"
                : isReviewMode
                  ? "border"
                  : "bg-transparent cursor-default grayscale opacity-40",
            )}
          >
            {emoji}
            {isSelected && (
              <span className="absolute -top-2 end-3 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                <CheckIcon size={9} className="text-white" strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
